// ============================================================
// equipmentData.js — equipment rows + schedule for the drawing export
//
// getEquipmentRowData moved here from ExportPage.jsx (unchanged) so the
// new sheet builder and the existing Equipment List share one source.
// buildEquipmentSchedule groups the LIVE placed items into schedule rows
// and assigns each a reference number (01, 02 …) used by the markers on
// the plan, so a marker can never point at a row that doesn't exist.
// ============================================================

import { CATALOG, CATS } from '../data/catalog';
import { BRAND_OPTIONS, DUTY_RATED_TYPES, SPEC_FIELDS } from '../data/brands';
import { ftIn } from './textMetrics';

// Works out what to show in each of the 4 table columns for one placed
// item, depending on which "kind" of equipment it is (brand-driven lift,
// duty-rated tire equipment, free-text-spec compressor, or plain generic
// item like a workbench). See brands.js for the three config objects this
// reads from — this function is just the "which one applies here" logic.
export const getEquipmentRowData = (item, catId, cat) => {
  const isDuty = DUTY_RATED_TYPES.includes(catId);
  const hasBrandList = !!BRAND_OPTIONS[catId];
  const hasSpecFields = !!SPEC_FIELDS[catId];

  // One fresh lookup, used for make/model/capacity/description/link below.
  // This is the important part: we look this up from BRAND_OPTIONS every
  // time using the stored brandKey, instead of trusting brandCapacity/
  // brandDescription/brandLink fields that got saved on the item back when
  // it was first picked. Those fields go stale the moment brands.js gets
  // better data later — which is exactly what happened here: tire
  // changer/balancer/aligner entries didn't have real descriptions when
  // some of these items were originally picked, so their saved
  // brandDescription was empty even after brands.js was updated with real
  // ones. Doing one fresh lookup here means every export always reflects
  // whatever's currently in brands.js, no re-picking required.
  let brandEntry = null;
  if (hasBrandList && item.brandKey) {
    const [bMake, bModel] = item.brandKey.split('|');
    brandEntry = (BRAND_OPTIONS[catId] || []).find(b => b.make === bMake && b.model === bModel) || null;
  }

  // Make / Model — from the picked brand if one was chosen, otherwise fall
  // back to whatever free-text label is set, otherwise "Generic".
  let make = 'Generic', model = '—';
  if (hasBrandList && item.brandKey) {
    const [m, mo] = item.brandKey.split('|');
    make = m; model = mo;
  } else if (item.label) {
    make = item.label; model = '—';
  }

  // Capacity — duty rating takes priority for tire equipment (that's the
  // whole point of the toggle), then the fresh brand capacity, then hp/tank
  // specs (compressor), then a plain dimension fallback for everything else
  // (benches, or a compressor before its specs are filled in).
  let capacity = '', capacityColor = null;
  if (isDuty) {
    capacity = item.dutyRating || '—';
    capacityColor = item.dutyRating === 'Heavy Duty' ? '#b36b00' : item.dutyRating === 'Light Duty' ? '#0f6e56' : null;
  } else if (hasBrandList && (brandEntry?.capacity || item.brandCapacity)) {
    capacity = brandEntry?.capacity || item.brandCapacity;
  } else {
    const fw = (cat?.fw || 0) * (item.scaleX || 1);
    const fh = (cat?.fh || 0) * (item.scaleY || 1);
    capacity = fw && fh ? `${fw.toFixed(1)}' × ${fh.toFixed(1)}'` : '—';
    // Compressor's HP/tank specs override the dimension fallback when filled
    if (hasSpecFields) {
      const specs = item.specs || {};
      const parts = [];
      if (specs.hp) parts.push(`${specs.hp} HP`);
      if (specs.tankSize) parts.push(`${specs.tankSize} tank`);
      if (parts.length) capacity = parts.join(' · ');
    }
  }

  // Description — fresh brand description first (this is what was missing
  // for tire changer/balancer/aligner — they have real descriptions in
  // brands.js now, this just wasn't reading them), or the spec "type"/
  // "notes" field for generic equipment, then always append catalog
  // requirements if present and not already covered.
  let description = '';
  if (hasBrandList && (brandEntry?.description || item.brandDescription)) {
    description = brandEntry?.description || item.brandDescription;
  } else if (hasSpecFields && item.specs?.type) description = item.specs.type;
  else if (hasSpecFields && item.specs?.notes) description = item.specs.notes;
  const reqs = (cat?.requirements || []).join(', ');
  if (reqs && !description.includes(reqs)) {
    description = description ? `${description} · ${reqs}` : reqs;
  }
  if (!description) description = cat?.name || '';

  // Product link — only set if a real brand was picked (Custom/Other or a
  // free-text label has nothing to link to). Same fresh-lookup fix as
  // capacity/description above.
  const link = brandEntry?.link || (hasBrandList ? item.brandLink : '') || '';

  return { make, model, capacity, capacityColor, description, link };
};

// Real (scaled) footprint of a placed item, in feet
const itemSize = (item) => ({
  w: (item.fw || 0) * (item.scaleX || 1),
  d: (item.fh || 0) * (item.scaleY || 1),
});

// Schedule rows from the live layout.
//   refMode 'type' — identical items share one row + one ref, with a quantity
//   refMode 'item' — every placed item gets its own ref and row
// Returns { rows, refByUid } where rows are in reference order.
export function buildEquipmentSchedule(placed, { refMode = 'type' } = {}) {
  const items = (placed || []).filter((p) => !p.isRoom);
  const catOrder = (id) => {
    const cat = CATALOG.find((c) => c.id === id);
    const i = cat ? CATS.indexOf(cat.cat) : -1;
    return i < 0 ? 99 : i;
  };

  const groups = new Map();
  items.forEach((item) => {
    const cat = CATALOG.find((c) => c.id === item.id);
    const row = getEquipmentRowData(item, item.id, cat);
    const { w, d } = itemSize(item);
    const name = item.label || cat?.name || item.name || 'Item';
    // Identical = same type, same chosen model/duty/specs, same real size, same name
    const key = refMode === 'item'
      ? item.uid
      : [item.id, item.brandKey || '', item.dutyRating || '', JSON.stringify(item.specs || {}),
         w.toFixed(2), d.toFixed(2), name].join('|');
    if (!groups.has(key)) {
      const hasModel = !!item.brandKey;
      // Notes only carry real data: a picked model's capacity, entered specs,
      // and the catalog's install requirements. Nothing is invented.
      const notes = [];
      if (hasModel && row.capacity && row.capacity !== '—') notes.push(row.capacity);
      if (DUTY_RATED_TYPES.includes(item.id) && item.dutyRating) notes.push(item.dutyRating);
      if (SPEC_FIELDS[item.id] && item.specs) {
        if (item.specs.hp) notes.push(`${item.specs.hp} HP`);
        if (item.specs.tankSize) notes.push(`${item.specs.tankSize} tank`);
        if (item.specs.type) notes.push(item.specs.type);
        if (item.specs.notes) notes.push(item.specs.notes);
      }
      (cat?.requirements || []).forEach((r) => notes.push(r));
      groups.set(key, {
        catId: item.id,
        name,
        model: hasModel ? `${row.make} ${row.model}` : '',
        size: `${ftIn(w)} × ${ftIn(d)}`,
        notes: notes.join(' · '),
        link: row.link || '',
        qty: 0,
        uids: [],
        sortCat: catOrder(item.id),
        sortPos: item.cy * 10000 + item.cx,
      });
    }
    const g = groups.get(key);
    g.qty += 1;
    g.uids.push(item.uid);
    g.sortPos = Math.min(g.sortPos, item.cy * 10000 + item.cx);
  });

  // Lifts first (catalog category order), then by name, then top-left first
  const rows = [...groups.values()].sort((a, b) =>
    a.sortCat - b.sortCat || a.name.localeCompare(b.name) || a.sortPos - b.sortPos);
  const refByUid = {};
  rows.forEach((r, i) => {
    r.ref = String(i + 1).padStart(2, '0');
    r.refLabel = `EQ-${r.ref}`;
    r.uids.forEach((u) => { refByUid[u] = r.ref; });
  });
  return { rows, refByUid };
}
